import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Chip, Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import { NotificationsActive as NotificationsActiveIcon, Email as EmailIcon, Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEmployeeAuth } from '../hooks/useEmployeeAuth';
import { employeePortalAPI } from '../utils/api';
import type { ShiftNotificationChannel } from '../types/employeePortal';

const CHANNELS: ShiftNotificationChannel[] = ['push', 'email', 'whatsapp'];

const CHANNEL_ICONS: Record<ShiftNotificationChannel, React.ReactElement> = {
  push: <NotificationsActiveIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  whatsapp: <i className="bi bi-whatsapp" />,
};

const panelSx = {
  borderRadius: 'var(--an-radius-md)',
  border: '1px solid var(--an-border-light)',
  boxShadow: 'var(--an-shadow-sm)',
  backgroundColor: 'var(--an-surface)',
};

const panelHeaderSx = {
  p: 2.25,
  backgroundColor: 'var(--an-surface)',
  borderBottom: '1px solid var(--an-border-light)',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const EmployeeNotificationSettings: React.FC = () => {
  const { t } = useTranslation('auth');
  const { employee } = useEmployeeAuth();

  const [channels, setChannels] = useState<ShiftNotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchPreferences = useCallback(async () => {
    if (!employee?.company_id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await employeePortalAPI.getShiftNotificationPreferences(employee.company_id);
      if (response.success) {
        setChannels(response.data.channels || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('employeePortal.preferencesLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [employee?.company_id, t]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const toggleChannel = (channel: ShiftNotificationChannel) => {
    setChannels((current) =>
      current.includes(channel)
        ? current.filter((c) => c !== channel)
        : [...current, channel]
    );
  };

  const handleSave = async () => {
    if (!employee?.company_id) return;
    setSaving(true);
    setError(null);
    try {
      await employeePortalAPI.updateShiftNotificationPreferences(employee.company_id, channels);
      setSuccessMessage(t('employeePortal.preferencesSaved'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('employeePortal.preferencesSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="div" sx={{ mt: 3 }}>
      <Box sx={panelSx}>
        <Box sx={panelHeaderSx}>
          <NotificationsActiveIcon fontSize="small" sx={{ color: 'var(--an-primary)' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">{t('employeePortal.notificationSettings')}</Typography>
            <Typography variant="caption" color="textSecondary">
              {t('employeePortal.notificationSettingsDesc')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2.25 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
                {CHANNELS.map((channel) => {
                  const selected = channels.includes(channel);
                  return (
                    <Chip
                      key={channel}
                      icon={CHANNEL_ICONS[channel]}
                      label={t(`employeePortal.channel${channel.charAt(0).toUpperCase()}${channel.slice(1)}`)}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleChannel(channel)}
                      sx={{ fontWeight: 700, '& .MuiChip-label': { fontWeight: 700 } }}
                    />
                  );
                })}
              </Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                disabled={saving || loading}
                onClick={handleSave}
                sx={{ textTransform: 'none', bgcolor: 'var(--an-primary)', fontWeight: 700 }}
              >
                {saving ? t('employeePortal.savingPreferences') : t('employeePortal.savePreferences')}
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
};

export default EmployeeNotificationSettings;
